import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  designReadyGameIds,
  getDesignReadyGames,
  getLevelsForGame,
} from '../../data/loadData';
import type { GameId } from '../../data/schemas';
import { hasPluss } from '../../store/entitlements';
import { useProgressStore } from '../../store/progress';
import { ParentGate } from '../../features/parentGate/ParentGate';
import { useProfileStore } from '../../store/profile';
import { levelsForAge } from '../../store/ageSettings';

function isGameId(value: string | undefined): value is GameId {
  return Boolean(value && (designReadyGameIds as string[]).includes(value));
}

export const LevelSelectScreen: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [gateOpen, setGateOpen] = useState(false);
  const isCompleted = useProgressStore((s) => s.isCompleted);
  const pluss = hasPluss();
  const ageBand = useProfileStore((s) => s.profile?.ageBand ?? null);

  if (!isGameId(gameId)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#8ecae6]">
        <p className="font-display text-2xl text-white">Ukjent spill</p>
        <Link to="/" className="rounded-2xl bg-white px-6 py-4 font-bold">
          Hjem
        </Link>
      </div>
    );
  }

  const meta = getDesignReadyGames().find((g) => g.id === gameId);
  const levels = levelsForAge(getLevelsForGame(gameId), ageBand);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#A8D8EA] to-[#669bbc] px-4 py-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold text-white drop-shadow">
              {meta?.title ?? gameId}
            </h1>
            <p className="font-body text-white/80 mt-1">Velg et nivå</p>
          </div>
          <Link
            to="/"
            className="inline-flex min-h-[96px] min-w-[96px] items-center justify-center rounded-2xl bg-white px-6 font-display text-xl font-bold text-[#6441A5] shadow"
          >
            Tilbake
          </Link>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 sm:gap-4">
          {levels.map((level) => {
            const locked = level.tier === 'pluss' && !pluss;
            const done = isCompleted(level.id);
            return (
              <button
                key={level.id}
                type="button"
                data-name={`level-card-${level.id}`}
                onClick={() => {
                  if (locked) {
                    setGateOpen(true);
                    return;
                  }
                  navigate(`/spill/${gameId}/${level.id}`);
                }}
                className={`relative min-h-[96px] rounded-2xl p-3 shadow transition ${
                  locked
                    ? 'bg-white/40 opacity-70'
                    : done
                      ? 'bg-[#FFE66D]'
                      : 'bg-white hover:shadow-lg'
                }`}
              >
                <span className="block font-display text-2xl font-bold text-gray-800">
                  {level.index}
                </span>
                <span className="block font-body text-xs text-gray-600 line-clamp-2 mt-1">
                  {level.title}
                </span>
                {locked ? (
                  <span className="absolute top-2 right-2 text-xl" aria-label="Låst">
                    🔒
                  </span>
                ) : null}
                {done && !locked ? (
                  <span className="absolute top-2 right-2 text-xl" aria-label="Fullført">
                    ⭐
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <ParentGate open={gateOpen} onClose={() => setGateOpen(false)} title="Enklespill Pluss" />
    </div>
  );
};

export default LevelSelectScreen;
